import { Login } from '../../apis/api-client';
import GroupSettingsRow from './GroupSettingsRow';
import UserAutocomplete from '../common/UserAutocomplete';
import { Box, Button, CircularProgress, Divider, InputAdornment, TextField, Typography } from '../ui';

interface GroupGeneralTabProps {
  // Controlled — fields live in the page (full-replace buildGroupAccessRequest
  // bundles the seat limit with branding/access on save).
  name: string;
  setName: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  // Group admin (owner) saves through its own endpoint independently of
  // the seat/access bundle, so it takes a change callback + a saving flag.
  groupAdmin: Login | null;
  groupAdminSaving: boolean;
  onChangeGroupAdmin: (next: Login | null) => void;
  seatLimitInput: string;
  setSeatLimitInput: (value: string) => void;
  /** Live member count (reported up by GroupMembersTab) for the caption. */
  memberCount: number;
  saving: boolean;
  onSave: () => void;
}

/**
 * GroupGeneralTab
 *
 * The General tab for the group editor: identity (name + description), the
 * group-admin (owner) picker, and the seat limit. Extracted from GroupEdit
 * as a CONTROLLED component. The owner picker saves immediately through its
 * own callback; the name/description/seat fields commit via the shared Save
 * Changes button (onSave).
 */
export default function GroupGeneralTab({
  name,
  setName,
  description,
  setDescription,
  groupAdmin,
  groupAdminSaving,
  onChangeGroupAdmin,
  seatLimitInput,
  setSeatLimitInput,
  memberCount,
  saving,
  onSave,
}: GroupGeneralTabProps) {
  return (
    <Box sx={{ maxWidth: 960, display: 'flex', flexDirection: 'column' }}>
      <GroupSettingsRow title="Identity" description="How the group is presented inside the workspace.">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Group Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — shown on the workspace's groups list."
          />
        </Box>
      </GroupSettingsRow>

      <Divider />

      <GroupSettingsRow
        title="Group admin"
        description="A learner who can view this group's roster and track its members' course progress. They can't add or remove members or change group settings."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <UserAutocomplete
            value={groupAdmin}
            onChange={(v) => onChangeGroupAdmin(Array.isArray(v) ? v[0] ?? null : v)}
            placeholder="Search learners…"
          />
          {groupAdminSaving && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Saving…
              </Typography>
            </Box>
          )}
          {!groupAdmin && !groupAdminSaving && (
            <Typography variant="caption" color="text.secondary">
              No admin assigned. The workspace's account admin manages this group by default.
            </Typography>
          )}
        </Box>
      </GroupSettingsRow>

      <Divider />

      <GroupSettingsRow
        title="Seat limit"
        description="Cap how many learners can be added to this group. Leave empty for unlimited."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              type="number"
              size="small"
              placeholder="Unlimited"
              value={seatLimitInput}
              onChange={(e) => setSeatLimitInput(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">seats</InputAdornment>,
              }}
              sx={{ width: 200 }}
            />
            {seatLimitInput.trim() && (
              <Typography variant="caption" color="text.secondary">
                {Math.max(0, parseInt(seatLimitInput, 10) - memberCount)} seat(s) remaining of {parseInt(seatLimitInput, 10)}.
              </Typography>
            )}
          </Box>
        </Box>
      </GroupSettingsRow>

      <Divider sx={{ mb: 3 }} />
      <Button
        variant="contained"
        onClick={onSave}
        disabled={saving}
        sx={{ alignSelf: 'flex-start', px: 3 }}
      >
        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
      </Button>
    </Box>
  );
}
